/**
 * Service d'envoi d'emails professionnels pour les devis
 * Intègre des templates HTML professionnels et des fonctionnalités d'envoi
 */

import { getCompanyInfo, getDefaultCompanyInfo } from './companyInfoService';
import { db } from '../firebase';
import { genererPDFDevis } from './devisPdfService';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Configuration du service d'email
 */
const EMAIL_CONFIG = {
    // Service d'email à utiliser (SendGrid, Mailgun, etc.)
    provider: 'sendgrid', // ou 'mailgun', 'smtp', etc.

    // Template par défaut
    defaultTemplate: 'devis-professionnel',

    // Options d'envoi
    options: {
        timeout: 30000, // 30 secondes
        retryAttempts: 3
    }
};

/**
 * Template HTML professionnel pour l'envoi de devis
 */
const EMAIL_TEMPLATES = {
    'devis-professionnel': (data) => {
        const { devis, companyInfo, pdfUrl } = data;

        return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Devis ${devis.numero} - ${companyInfo.denominationSociale}</title>
    <style>
        /* Reset CSS */
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            margin: 0;
            padding: 0;
        }
        
        .email-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        /* En-tête */
        .email-header {
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            padding: 30px;
            text-align: center;
        }
        
        .company-logo {
            max-width: 120px;
            max-height: 60px;
            margin-bottom: 15px;
        }
        
        .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        
        .company-tagline {
            font-size: 14px;
            opacity: 0.9;
        }
        
        /* Contenu principal */
        .email-content {
            padding: 30px;
        }
        
        .greeting {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #1f2937;
        }
        
        .message {
            font-size: 15px;
            line-height: 1.7;
            color: #4b5563;
            margin-bottom: 25px;
        }
        
        .devis-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
        }
        
        .devis-title {
            font-size: 20px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 10px;
        }
        
        .devis-details {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            font-size: 14px;
        }
        
        .devis-detail {
            margin-bottom: 8px;
        }
        
        .devis-detail strong {
            color: #374151;
        }
        
        .devis-amount {
            font-size: 24px;
            font-weight: bold;
            color: #059669;
            text-align: center;
            margin: 15px 0;
        }
        
        /* Boutons d'action */
        .action-buttons {
            text-align: center;
            margin: 30px 0;
        }
        
        .btn {
            display: inline-block;
            padding: 12px 30px;
            background: linear-gradient(135deg, #3b82f6, #1d4ed8);
            color: white;
            text-decoration: none;
            border-radius: 6px;
            font-weight: 600;
            font-size: 15px;
            margin: 0 10px;
            transition: all 0.3s ease;
        }
        
        .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        .btn-secondary {
            background: linear-gradient(135deg, #6b7280, #4b5563);
        }
        
        .btn-secondary:hover {
            box-shadow: 0 4px 12px rgba(107, 114, 128, 0.4);
        }
        
        /* Informations importantes */
        .important-info {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 8px;
            padding: 15px;
            margin: 20px 0;
        }
        
        .important-title {
            font-weight: bold;
            color: #92400e;
            margin-bottom: 8px;
        }
        
        .important-content {
            font-size: 14px;
            color: #92400e;
        }
        
        /* Pied de page */
        .email-footer {
            background: #f1f5f9;
            padding: 25px;
            text-align: center;
            border-top: 1px solid #e2e8f0;
        }
        
        .footer-text {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.5;
        }
        
        .contact-info {
            margin-top: 15px;
            font-size: 13px;
            color: #4b5563;
        }
        
        .social-links {
            margin-top: 15px;
        }
        
        .social-link {
            display: inline-block;
            margin: 0 8px;
            color: #6b7280;
            text-decoration: none;
            font-size: 13px;
        }
        
        .social-link:hover {
            color: #3b82f6;
        }
        
        /* Responsive */
        @media (max-width: 600px) {
            .email-container {
                margin: 10px;
                border-radius: 8px;
            }
            
            .email-header,
            .email-content {
                padding: 20px;
            }
            
            .devis-details {
                grid-template-columns: 1fr;
            }
            
            .action-buttons {
                text-align: center;
            }
            
            .btn {
                display: block;
                margin: 10px 0;
            }
        }
    </style>
</head>
<body>
    <div class="email-container">
        <!-- En-tête -->
        <div class="email-header">
            ${companyInfo.logoUrl ? `<img src="${companyInfo.logoUrl}" alt="${companyInfo.denominationSociale}" class="company-logo">` : ''}
            <div class="company-name">${companyInfo.denominationSociale}</div>
            <div class="company-tagline">${companyInfo.slogan || 'Votre partenaire de confiance'}</div>
        </div>
        
        <!-- Contenu principal -->
        <div class="email-content">
            <div class="greeting">
                Bonjour ${devis.clientNom},
            </div>
            
            <div class="message">
                Nous vous remercions de l'intérêt que vous portez à nos services. 
                Comme convenu, nous avons le plaisir de vous transmettre notre devis détaillé 
                pour les prestations que vous avez sollicitées.
            </div>
            
            <!-- Carte du devis -->
            <div class="devis-card">
                <div class="devis-title">Devis N° ${devis.numero}</div>
                
                <div class="devis-details">
                    <div class="devis-detail">
                        <strong>Date d'émission :</strong><br>
                        ${new Date(devis.dateCreation).toLocaleDateString('fr-FR')}
                    </div>
                    <div class="devis-detail">
                        <strong>Validité :</strong><br>
                        ${devis.dateValidite ? new Date(devis.dateValidite).toLocaleDateString('fr-FR') : '30 jours'}
                    </div>
                </div>
                
                <div class="devis-amount">
                    ${devis.montantTTC ? `Total : ${devis.montantTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}` : 'Montant à consulter'}
                </div>
                
                <div class="devis-details">
                    <div class="devis-detail">
                        <strong>Type de prestation :</strong><br>
                        Services à la personne
                    </div>
                    <div class="devis-detail">
                        <strong>Volume estimé :</strong><br>
                        ${devis.calculs?.totaux?.totalHeures || 'N/A'} heures
                    </div>
                </div>
            </div>
            
            <!-- Informations importantes -->
            <div class="important-info">
                <div class="important-title">💡 Informations importantes</div>
                <div class="important-content">
                    • Ce devis est valable ${devis.dateValidite ? `jusqu'au ${new Date(devis.dateValidite).toLocaleDateString('fr-FR')}` : '30 jours'}<br>
                    • Bénéficiez d'un crédit d'impôt de 50% sur les services à la personne<br>
                    • Réponse souhaitée sous 7 jours ouvrés
                </div>
            </div>
            
            <!-- Boutons d'action -->
            <div class="action-buttons">
                <a href="${pdfUrl}" class="btn" target="_blank">
                    📄 Télécharger le devis
                </a>
                <a href="mailto:${companyInfo.email}?subject=Question%20sur%20le%20devis%20${devis.numero}" class="btn btn-secondary">
                    💬 Poser une question
                </a>
            </div>
            
            <div class="message">
                Pour toute question ou modification, n'hésitez pas à nous contacter. 
                Nous restons à votre disposition pour échanger sur ce devis et adapter 
                nos prestations à vos besoins spécifiques.
            </div>
        </div>
        
        <!-- Pied de page -->
        <div class="email-footer">
            <div class="footer-text">
                <strong>${companyInfo.denominationSociale}</strong><br>
                ${companyInfo.siegeSocial}<br>
                ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''}
                ${companyInfo.email ? ` • Email : ${companyInfo.email}` : ''}
            </div>
            
            <div class="contact-info">
                SIREN : ${companyInfo.siren} ${companyInfo.siret ? `• SIRET : ${companyInfo.siret}` : ''}<br>
                ${companyInfo.numeroSAP ? `Numéro SAP : ${companyInfo.numeroSAP}` : ''}
            </div>
            
            <div class="social-links">
                ${companyInfo.siteWeb ? `<a href="${companyInfo.siteWeb}" class="social-link" target="_blank">🌐 Site web</a>` : ''}
                ${companyInfo.linkedin ? `<a href="${companyInfo.linkedin}" class="social-link" target="_blank">💼 LinkedIn</a>` : ''}
            </div>
        </div>
    </div>
</body>
</html>
    `;
    },

    'devis-relance': (data) => {
        const { devis, companyInfo, pdfUrl } = data;

        return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Relance devis ${devis.numero} - ${companyInfo.denominationSociale}</title>
    <style>
        /* Styles similaires au template principal mais avec accent sur l'urgence */
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            margin: 0;
            padding: 0;
        }

        .email-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .email-header {
            background: linear-gradient(135deg, #f59e0b, #d97706);
            color: white;
            padding: 30px;
            text-align: center;
        }

        /* ... autres styles similaires au template principal ... */
    </style>
</head>
<body>
    <div class="email-container">
        <div class="email-header">
            <div class="company-name">${companyInfo.denominationSociale}</div>
            <div class="company-tagline">Rappel - Devis en attente</div>
        </div>

        <div class="email-content">
            <div class="greeting">
                Bonjour ${devis.clientNom},
            </div>

            <div class="message">
                Nous souhaitons vous rappeler notre devis N° <strong>${devis.numero}</strong>
                qui vous a été transmis le ${new Date(devis.dateCreation).toLocaleDateString('fr-FR')}.
            </div>

            <div class="important-info" style="background: #fef3c7; border-color: #f59e0b;">
                <div class="important-title">⏰ Délai de validité</div>
                <div class="important-content">
                    Ce devis est valable jusqu'au <strong>${devis.dateValidite ? new Date(devis.dateValidite).toLocaleDateString('fr-FR') : '30 jours'}</strong>.
                    Nous vous invitons à nous faire part de votre décision dans les meilleurs délais.
                </div>
            </div>

            <div class="action-buttons">
                <a href="${pdfUrl}" class="btn" target="_blank">
                    📄 Consulter le devis
                </a>
                <a href="mailto:${companyInfo.email}?subject=Réponse%20devis%20${devis.numero}" class="btn btn-secondary">
                    💬 Répondre au devis
                </a>
            </div>
        </div>

        <div class="email-footer">
            <!-- Pied de page similaire au template principal -->
        </div>
    </div>
</body>
</html>
    `;
    },

    'contrat-mandat': (data) => {
        const { beneficiaryName, companyInfo, signatureUrl } = data;

        return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contrat de Mandat - ${companyInfo.denominationSociale}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            background-color: #f8fafc;
            margin: 0;
            padding: 0;
        }

        .email-container {
            max-width: 600px;
            margin: 0 auto;
            background-color: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .email-header {
            background: linear-gradient(135deg, #10b981, #059669);
            color: white;
            padding: 30px;
            text-align: center;
        }

        .company-logo {
            max-width: 120px;
            max-height: 60px;
            margin-bottom: 15px;
        }

        .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
        }

        .company-tagline {
            font-size: 14px;
            opacity: 0.9;
        }

        .email-content {
            padding: 30px;
        }

        .greeting {
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #1f2937;
        }

        .message {
            font-size: 15px;
            line-height: 1.7;
            color: #4b5563;
            margin-bottom: 20px;
        }

        .document-card {
            background: #f0fdf4;
            border: 2px solid #10b981;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            text-align: center;
        }

        .document-icon {
            font-size: 48px;
            margin-bottom: 10px;
        }

        .document-title {
            font-size: 20px;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 10px;
        }

        .document-description {
            font-size: 14px;
            color: #6b7280;
            margin-bottom: 20px;
        }

        .action-buttons {
            text-align: center;
            margin: 30px 0;
        }

        .btn {
            display: inline-block;
            padding: 12px 30px;
            background: linear-gradient(135deg, #10b981, #059669);
            color: white;
            text-decoration: none;
            border-radius: 6px;
            font-weight: 600;
            font-size: 15px;
            margin: 5px;
            transition: all 0.3s ease;
        }

        .btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .btn-secondary {
            background: linear-gradient(135deg, #6b7280, #4b5563);
        }

        .btn-secondary:hover {
            box-shadow: 0 4px 12px rgba(107, 114, 128, 0.4);
        }

        .important-info {
            background: #dbeafe;
            border: 1px solid #3b82f6;
            border-radius: 8px;
            padding: 15px;
            margin: 20px 0;
        }

        .important-title {
            font-weight: bold;
            color: #1e40af;
            margin-bottom: 8px;
        }

        .important-content {
            font-size: 14px;
            color: #1e40af;
        }

        .steps-container {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
        }

        .step {
            display: flex;
            align-items: flex-start;
            margin-bottom: 15px;
        }

        .step-number {
            background: #10b981;
            color: white;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            margin-right: 15px;
            flex-shrink: 0;
        }

        .step-content {
            flex: 1;
        }

        .step-title {
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 3px;
        }

        .step-description {
            font-size: 14px;
            color: #6b7280;
        }

        .email-footer {
            background: #f1f5f9;
            padding: 25px;
            text-align: center;
            border-top: 1px solid #e2e8f0;
        }

        .footer-text {
            font-size: 13px;
            color: #6b7280;
            line-height: 1.5;
        }

        .contact-info {
            margin-top: 15px;
            font-size: 13px;
            color: #4b5563;
        }

        @media (max-width: 600px) {
            .email-container {
                margin: 10px;
                border-radius: 8px;
            }

            .email-header,
            .email-content {
                padding: 20px;
            }

            .btn {
                display: block;
                margin: 10px 0;
            }
        }
    </style>
</head>
<body>
    <div class="email-container">
        <!-- En-tête -->
        <div class="email-header">
            ${companyInfo.logoUrl ? `<img src="${companyInfo.logoUrl}" alt="${companyInfo.denominationSociale}" class="company-logo">` : ''}
            <div class="company-name">${companyInfo.denominationSociale}</div>
            <div class="company-tagline">Contrat de Mandat - Services à la Personne</div>
        </div>

        <!-- Contenu principal -->
        <div class="email-content">
            <div class="greeting">
                Bonjour ${beneficiaryName},
            </div>

            <div class="message">
                Nous sommes heureux de vous accompagner dans la mise en place de vos services à domicile.
                Comme convenu, vous trouverez ci-joint votre <strong>Contrat de Mandat</strong> personnalisé.
            </div>

            <!-- Carte du document -->
            <div class="document-card">
                <div class="document-icon">📄</div>
                <div class="document-title">Contrat de Mandat</div>
                <div class="document-description">
                    Document contractuel définissant le cadre de notre collaboration
                </div>
            </div>

            <!-- Informations importantes -->
            <div class="important-info">
                <div class="important-title">📋 À propos du contrat de mandat</div>
                <div class="important-content">
                    Ce contrat définit le cadre de notre intervention en mode mandataire.
                    Vous restez l'employeur de l'intervenant à domicile, et nous vous accompagnons
                    dans la coordination et le suivi qualité des prestations.
                </div>
            </div>

            <!-- Étapes suivantes -->
            <div class="steps-container">
                <h3 style="margin-bottom: 15px; color: #1f2937;">Prochaines étapes :</h3>

                <div class="step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                        <div class="step-title">Consulter le contrat</div>
                        <div class="step-description">Téléchargez et lisez attentivement le contrat de mandat en pièce jointe</div>
                    </div>
                </div>

                <div class="step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                        <div class="step-title">Prendre connaissance des annexes</div>
                        <div class="step-description">Consultez le livret d'accueil, la charte des droits et le règlement intérieur</div>
                    </div>
                </div>

                <div class="step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                        <div class="step-title">Signer le contrat</div>
                        <div class="step-description">Cliquez sur le lien ci-dessous pour signer électroniquement le contrat</div>
                    </div>
                </div>

                <div class="step">
                    <div class="step-number">4</div>
                    <div class="step-content">
                        <div class="step-title">Validation et début des prestations</div>
                        <div class="step-description">Une fois signé, nous pourrons débuter les interventions à domicile</div>
                    </div>
                </div>
            </div>

            <!-- Boutons d'action -->
            <div class="action-buttons">
                ${signatureUrl ? `
                <a href="${signatureUrl}" class="btn" target="_blank">
                    ✍️ Signer le contrat en ligne
                </a>
                ` : ''}
                <a href="mailto:${companyInfo.email}?subject=Question%20sur%20le%20contrat%20de%20mandat" class="btn btn-secondary">
                    💬 Poser une question
                </a>
            </div>

            <div class="message">
                Pour toute question ou besoin d'accompagnement, notre équipe reste à votre entière disposition.
                N'hésitez pas à nous contacter par téléphone ou par email.
            </div>

            <div class="important-info">
                <div class="important-title">⏰ Droit de rétractation</div>
                <div class="important-content">
                    Conformément à la législation, vous disposez d'un délai de rétractation de <strong>14 jours</strong>
                    à compter de la signature du contrat.
                </div>
            </div>
        </div>

        <!-- Pied de page -->
        <div class="email-footer">
            <div class="footer-text">
                <strong>${companyInfo.denominationSociale}</strong><br>
                ${companyInfo.siegeSocial}<br>
                ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''}
                ${companyInfo.email ? ` • Email : ${companyInfo.email}` : ''}
            </div>

            <div class="contact-info">
                SIREN : ${companyInfo.siren} ${companyInfo.siret ? `• SIRET : ${companyInfo.siret}` : ''}<br>
                ${companyInfo.numeroSAP ? `Numéro SAP : ${companyInfo.numeroSAP}` : ''}
            </div>
        </div>
    </div>
</body>
</html>
    `;
    }
};

/**
 * Service d'envoi d'emails professionnels
 */
class EmailService {
    constructor(db) {
        this.db = db;
    }

    /**
     * Envoyer un devis par email
     */
    async envoyerDevis(devis, options = {}) {
        try {
            console.log('📧 Préparation envoi email pour le devis:', devis.numero);

            // Récupérer les informations de la société
            const companyInfo = await getCompanyInfo(this.db);

            // Générer le PDF du devis
            const pdfUrl = await this.genererPDFPourEmail(devis);

            // Préparer les données pour le template
            const templateData = {
                devis,
                companyInfo,
                pdfUrl,
                options
            };

            // Sélectionner le template
            const templateName = options.template || EMAIL_CONFIG.defaultTemplate;
            const htmlContent = EMAIL_TEMPLATES[templateName](templateData);

            // Préparer l'email
            const emailData = {
                to: options.destinataire || devis.clientEmail,
                subject: this.genererSujetEmail(devis, companyInfo, options),
                html: htmlContent,
                attachments: options.attachments || []
            };

            // Ajouter le PDF en pièce jointe si demandé
            if (options.attacherPDF !== false) {
                emailData.attachments.push({
                    filename: `Devis_${devis.numero}_${companyInfo.sigle}.pdf`,
                    path: pdfUrl,
                    contentType: 'application/pdf'
                });
            }

            // Validation des champs obligatoires
            if (!emailData.to) {
                throw new Error('Adresse email du client manquante. Veuillez vérifier la fiche client.');
            }
            if (!emailData.subject) {
                throw new Error('Sujet de l\'email manquant.');
            }
            if (!emailData.html) {
                throw new Error('Contenu de l\'email manquant.');
            }

            // Envoyer l'email
            const resultat = await this.envoyerEmail(emailData);

            console.log('✅ Email envoyé avec succès pour le devis:', devis.numero);
            return resultat;

        } catch (error) {
            console.error('❌ Erreur envoi email devis:', error);
            throw new Error(`Échec de l'envoi de l'email: ${error.message}`);
        }
    }

    /**
     * Générer un PDF pour l'envoi par email
     */
    async genererPDFPourEmail(devis) {
        try {
            console.log('🎯 Génération PDF pour email, devis:', devis.numero);

            // Utiliser le service existant de génération de PDF avec l'option returnBlob
            const pdfBlob = await genererPDFDevis(devis, {
                returnBlob: true,
                companyInfo: await getCompanyInfo(this.db)
            });

            console.log('✅ PDF généré, type:', typeof pdfBlob, 'taille:', pdfBlob?.size);

            // Vérifier que le PDF a bien été généré en tant que Blob
            if (!pdfBlob || !(pdfBlob instanceof Blob)) {
                throw new Error('Le PDF généré n\'est pas un Blob valide');
            }

            // Créer une URL temporaire pour le PDF
            const pdfUrl = URL.createObjectURL(pdfBlob);
            console.log('✅ URL PDF créée:', pdfUrl);

            return pdfUrl;
        } catch (error) {
            console.error('❌ Erreur génération PDF pour email:', error);
            // En cas d'erreur, retourner une URL vide pour éviter de bloquer l'envoi d'email
            return '';
        }
    }

    /**
     * Générer le sujet de l'email
     */
    genererSujetEmail(devis, companyInfo, options = {}) {
        const prefix = options.relance ? '[RAPPEL] ' : '';
        const type = options.type || 'devis';

        return `${prefix}${type.charAt(0).toUpperCase() + type.slice(1)} ${devis.numero} - ${companyInfo.denominationSociale}`;
    }

    /**
     * Récupérer la configuration IMAP/SMTP depuis les paramètres
     */
    async getEmailConfig() {
        try {
            const settingsDoc = await getDoc(doc(this.db, 'settings', 'general'));
            if (settingsDoc.exists()) {
                const settings = settingsDoc.data();

                // Vérifier si la configuration IMAP/SMTP est complète
                const hasIMAPConfig = settings.smtpHost && settings.smtpUsername && settings.smtpPassword;

                if (hasIMAPConfig) {
                    return {
                        provider: 'smtp',
                        host: settings.smtpHost,
                        port: settings.smtpPort || 587,
                        username: settings.smtpUsername,
                        password: settings.smtpPassword,
                        fromName: settings.emailFromName || settings.denominationSociale,
                        fromAddress: settings.emailFromAddress || settings.email,
                        useSSL: settings.emailUseSSL !== false,
                        useTLS: settings.emailUseTLS !== false
                    };
                }
            }

            // Retourner la configuration par défaut si pas de configuration IMAP
            return {
                provider: 'simulation',
                message: 'Configuration IMAP/SMTP non trouvée, utilisation de la simulation'
            };
        } catch (error) {
            console.error('Erreur récupération configuration email:', error);
            return {
                provider: 'simulation',
                message: 'Erreur configuration, utilisation de la simulation'
            };
        }
    }

    /**
     * Envoyer un email via le service configuré
     */
    async envoyerEmail(emailData) {
        try {
            // Préparer les pièces jointes
            const preparedAttachments = await this.preparerAttachments(emailData.attachments);

            // Calculer la taille approximative des pièces jointes
            let totalAttachmentSize = 0;
            preparedAttachments.forEach(att => {
                if (att.content) {
                    totalAttachmentSize += att.content.length; // taille en base64
                }
            });

            console.log('📤 Envoi email via Firebase Functions:', {
                to: emailData.to,
                subject: emailData.subject,
                attachments: preparedAttachments.length,
                attachmentSizeKB: Math.round(totalAttachmentSize / 1024),
                htmlSizeKB: Math.round((emailData.html?.length || 0) / 1024)
            });

            // Vérifier la taille totale du payload (limite httpsCallable ~10MB)
            const MAX_PAYLOAD_SIZE = 9 * 1024 * 1024; // 9MB pour laisser de la marge
            if (totalAttachmentSize > MAX_PAYLOAD_SIZE) {
                console.warn('⚠️ Pièces jointes trop volumineuses, envoi sans pièces jointes');
                preparedAttachments.length = 0; // Vider les pièces jointes
            }

            // Utiliser la fonction Firebase pour l'envoi d'email
            const sendEmailFunction = httpsCallable(functions, 'sendInvoiceEmail');

            // Préparer les données pour la fonction Firebase
            const emailPayload = {
                to: emailData.to,
                subject: emailData.subject,
                html: emailData.html,
                text: this.htmlToText(emailData.html),
                attachments: preparedAttachments
            };

            // Appeler la fonction Firebase
            const result = await sendEmailFunction(emailPayload);

            console.log('✅ Email envoyé via Firebase Functions:', result.data);

            if (result.data?.rejected?.length > 0) {
                console.warn('⚠️ Email rejeté par certains destinataires:', result.data.rejected);
            }

            return {
                success: true,
                messageId: result.data.messageId,
                status: 'sent',
                timestamp: new Date(),
                provider: 'firebase-functions',
                accepted: result.data.accepted,
                rejected: result.data.rejected
            };

        } catch (error) {
            // Extraire un message d'erreur lisible
            let errorMessage = error.message;
            if (error.code === 'functions/internal') {
                errorMessage = `Erreur serveur d'envoi: ${error.message}`;
            } else if (error.code === 'functions/failed-precondition') {
                errorMessage = `Configuration email manquante: ${error.message}. Vérifiez les paramètres SMTP dans Paramètres > Email.`;
            } else if (error.code === 'functions/unauthenticated') {
                errorMessage = 'Vous devez être connecté pour envoyer des emails.';
            } else if (error.code === 'functions/invalid-argument') {
                errorMessage = `Données d'email invalides: ${error.message}`;
            }

            console.error('❌ Erreur envoi email via Firebase Functions:', {
                code: error.code,
                message: errorMessage,
                details: error.details
            });
            throw new Error(errorMessage);
        }
    }

    /**
     * Envoyer un email avec SMTP
     */
    async envoyerAvecSMTP(emailData, config) {
        try {
            console.log('📤 Envoi email via SMTP:', {
                host: config.host,
                port: config.port,
                to: emailData.to,
                subject: emailData.subject
            });

            // Note: Pour une implémentation réelle, vous devrez installer et utiliser nodemailer
            // const nodemailer = require('nodemailer');

            // Exemple d'implémentation avec nodemailer (à décommenter et adapter):
            /*
            const transporter = nodemailer.createTransport({
              host: config.host,
              port: config.port,
              secure: config.useSSL,
              auth: {
                user: config.username,
                pass: config.password
              },
              tls: {
                rejectUnauthorized: config.useTLS
              }
            });
            
            const mailOptions = {
              from: `"${config.fromName}" <${config.fromAddress}>`,
              to: emailData.to,
              subject: emailData.subject,
              html: emailData.html,
              attachments: emailData.attachments
            };
            
            const result = await transporter.sendMail(mailOptions);
            
            return {
              success: true,
              messageId: result.messageId,
              status: 'sent',
              timestamp: new Date(),
              provider: 'smtp'
            };
            */

            // Simulation pour l'instant
            await new Promise(resolve => setTimeout(resolve, 2000));

            return {
                success: true,
                messageId: `smtp-${Date.now()}`,
                status: 'sent',
                timestamp: new Date(),
                provider: 'smtp',
                note: 'Configuration SMTP détectée - Implémentation nodemailer requise'
            };

        } catch (error) {
            console.error('Erreur envoi SMTP:', error);
            throw error;
        }
    }

    /**
     * Convertir HTML en texte brut pour l'alternative textuelle
     */
    htmlToText(html) {
        // Conversion simple du HTML en texte
        return html
            .replace(/<style[^>]*>.*?<\/style>/gs, '')
            .replace(/<script[^>]*>.*?<\/script>/gs, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /**
     * Préparer les pièces jointes pour l'envoi via Firebase Functions
     */
    async preparerAttachments(attachments) {
        if (!attachments || attachments.length === 0) {
            return [];
        }

        const preparedAttachments = [];

        for (const attachment of attachments) {
            if (attachment.path && attachment.path.startsWith('blob:')) {
                // Convertir l'URL blob en base64
                try {
                    const response = await fetch(attachment.path);
                    const blob = await response.blob();
                    const arrayBuffer = await blob.arrayBuffer();
                    const uint8Array = new Uint8Array(arrayBuffer);

                    // Conversion par chunks pour éviter "Maximum call stack size exceeded"
                    let binary = '';
                    const chunkSize = 8192; // 8KB chunks
                    for (let i = 0; i < uint8Array.length; i += chunkSize) {
                        const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
                        binary += String.fromCharCode.apply(null, Array.from(chunk));
                    }
                    const base64 = btoa(binary);

                    preparedAttachments.push({
                        filename: attachment.filename,
                        content: base64,
                        contentType: attachment.contentType || 'application/pdf'
                    });
                } catch (error) {
                    console.error('Erreur conversion pièce jointe:', error);
                }
            } else {
                // Pour les autres types de pièces jointes
                preparedAttachments.push({
                    filename: attachment.filename,
                    path: attachment.path,
                    contentType: attachment.contentType
                });
            }
        }

        return preparedAttachments;
    }

    /**
     * Envoyer une relance pour un devis
     */
    async envoyerRelance(devis, options = {}) {
        try {
            console.log('🔔 Envoi relance pour le devis:', devis.numero);

            const emailOptions = {
                ...options,
                template: 'devis-relance',
                relance: true,
                type: 'relance devis'
            };

            return await this.envoyerDevis(devis, emailOptions);
        } catch (error) {
            console.error('Erreur envoi relance:', error);
            throw error;
        }
    }

    /**
     * Vérifier la configuration du service d'email
     */
    async verifierConfiguration() {
        const companyInfo = await getCompanyInfo(this.db);

        const configuration = {
            service: EMAIL_CONFIG.provider,
            company: {
                nom: companyInfo.denominationSociale,
                email: companyInfo.email,
                telephone: companyInfo.telephone
            },
            templates: Object.keys(EMAIL_TEMPLATES),
            statut: 'configuré'
        };

        console.log('🔧 Configuration email service:', configuration);
        return configuration;
    }

    /**
     * Tester l'envoi d'email avec un devis de test
     */
    async testerEnvoiEmail(devisTest = null) {
        try {
            console.log('🧪 Test envoi email...');

            // Créer un devis de test si non fourni
            const devis = devisTest || {
                id: 'test-' + Date.now(),
                numero: 'DEV-TEST-001',
                clientNom: 'Client Test',
                clientEmail: 'test@example.com',
                dateCreation: new Date(),
                dateValidite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                montantTTC: 1500,
                calculs: {
                    totaux: {
                        totalHeures: 40
                    }
                },
                statut: 'brouillon'
            };

            const resultat = await this.envoyerDevis(devis, {
                template: 'devis-professionnel',
                attacherPDF: false // Pas de PDF en test
            });

            console.log('✅ Test email réussi:', resultat);
            return resultat;

        } catch (error) {
            console.error('❌ Test email échoué:', error);
            throw error;
        }
    }
}

// Export du service
export default EmailService;

// Export des templates pour utilisation externe
export { EMAIL_TEMPLATES, EMAIL_CONFIG };
